import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import ProtectedRoute from './components/routes/ProtectedRoute'
import Layout from './components/layout/Layout'
import LoginPage from './features/auth/pages/LoginPage'
import ImpersonatePage from './features/auth/pages/ImpersonatePage'
import TokenExpiredModal from './components/auth/TokenExpiredModal'
import { setTokenExpiredHandler } from './api/client'

// Lazy load pages for better performance
import { lazy, Suspense, useEffect } from 'react'
import Skeleton from './components/ui/Skeleton'

const DashboardPage = lazy(() => import('./features/dashboard/pages/DashboardPage'))
const TransactionsPage = lazy(() => import('./features/transactions/pages/TransactionsPage'))
const PayoutsPage = lazy(() => import('./features/payouts/pages/PayoutsPage'))
const FundTransferPage = lazy(() => import('./features/fund-transfer/pages/FundTransferPage'))
const BeneficiariesPage = lazy(() => import('./features/beneficiaries/pages/BeneficiariesPage'))
const SettlementsPage = lazy(() => import('./features/settlements/pages/SettlementsPage'))
const PassbookPage = lazy(() => import('./features/passbook/pages/PassbookPage'))
const SettingsPage = lazy(() => import('./features/settings/pages/SettingsPage'))

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="space-y-4 w-64">
      <Skeleton variant="heading" className="w-full" />
      <Skeleton variant="text" className="w-3/4" />
      <Skeleton variant="text" className="w-1/2" />
    </div>
  </div>
)

const AppContent = () => {
  const { isTokenExpired, setTokenExpired } = useAuth()

  useEffect(() => {
    // Set up token expiration handler
    setTokenExpiredHandler(setTokenExpired)
  }, [setTokenExpired])

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Navigate to="/dashboard" replace />
              </Suspense>
            }
          />
          <Route
            path="dashboard"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route
            path="transactions"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <TransactionsPage />
              </Suspense>
            }
          />
          <Route
            path="payouts"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PayoutsPage />
              </Suspense>
            }
          />
          <Route
            path="fund-transfer"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <FundTransferPage />
              </Suspense>
            }
          />
          <Route
            path="beneficiaries"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <BeneficiariesPage />
              </Suspense>
            }
          />
          <Route
            path="settlements"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <SettlementsPage />
              </Suspense>
            }
          />
          <Route
            path="passbook"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PassbookPage />
              </Suspense>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <SettingsPage />
              </Suspense>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <TokenExpiredModal isOpen={isTokenExpired} />
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App

